import { Tooltip } from "primereact/tooltip";
import { CSSProperties, useEffect, useState } from "react";

export interface ThermometerItem {
    color: string;
    label: string;
    value: string | number;
    toolTip?: string;
}


export interface ThermometerProps {
    value: ThermometerItem[];
    className?: string;
    style?: CSSProperties;
    valueStyle?: CSSProperties;
    labelStyle?: CSSProperties;
    valueClassName?: string;
    labelClassName?: string;
    id?: string;
    onClick?: (item: ThermometerItem, index: number) => void;
    direction: 'vertical' | 'horizontal';
}


/**
 * Displays a stack of labeled metrics with color-coded bars arranged vertically or horizontally.
 * @param props - Data points to render along with layout, styling, and click handling options.
 * @returns A thermometer-style visualization that reflows according to the chosen direction.
 */
export function Thermometer(props: ThermometerProps) {

    const [_value, setValue] = useState<ThermometerItem[]>(props.value || []);
    const [_id, setId] = useState<string>(props.id || `fbx-thermometer`);
    const [_direction, setDirection] = useState<'vertical' | 'horizontal'>(props.direction || 'horizontal');
    const [_dirClassName, setDirClassName] = useState<string>('fbx-thermometer-horizontal');

    useEffect(() => { setValue(props.value || []) }, [props.value]);
    useEffect(() => { setId(props.id || `fbx-thermometer`) }, [props.id]);
    useEffect(() => {
        setDirection(props.direction || 'horizontal')
        setDirClassName(props.direction === 'vertical' ? 'fbx-thermometer-vertical' : 'fbx-thermometer-horizontal')
    }, [props.direction]);


    /**
     * Invokes the optional click handler with the selected item context.
     * @param item - Thermometer segment that was interacted with.
     * @param index - Position of the segment within the data array.
     */
    const handleClick = (item: ThermometerItem, index: number) => {
        if (props.onClick) {
            props.onClick(item, index);
        }

    }


    /**
     * Renders a thermometer segment as a stacked column layout for horizontal orientation.
     * @param item - Data describing the segment label, value, and color.
     * @param index - Position of the item used to derive tooltip identity.
     * @returns JSX representing the horizontal thermometer entry and tooltip binding.
     */
    const renderHorizontal = (item: ThermometerItem, index: number) => {
        let toolTipId = `${_id}-${index}`
        return (<>
            <span key={toolTipId} id={toolTipId} className={`flex flex-column fbx-thermometer-item`}  onClick={() => handleClick(item, index)}>

                <span className={`flex align-items-center justify-content-center ${props.labelClassName ?? ""} fbx-thermometer-item-label`} style={props.labelStyle ?? {}}>{item.label}</span>
                <span style={{ backgroundColor: item.color, ...props.valueStyle }}
                    className={`flex align-items-center justify-content-center ${props.valueClassName ?? ""}  fbx-thermometer-item-value`}>{item.value}</span>
            </span>
            <Tooltip target={`#${toolTipId}`} content={item.toolTip || ''} />
        </>)
    }


    /**
     * Renders a thermometer segment as a row layout for vertical orientation.
     * @param item - Data describing the segment label, value, and color.
     * @param index - Position of the item used to derive tooltip identity.
     * @returns JSX representing the vertical thermometer entry and tooltip binding.
     */
     const renderVertical = (item: ThermometerItem, index: number,) => {
        let toolTipId = `${_id}-${index}`
        return (<>
            <span key={toolTipId} id={toolTipId} className={`flex flex-row fbx-thermometer-item`} onClick={() => handleClick(item, index)}>

                <span className={`flex align-items-center justify-content-end ${props.labelClassName ?? ""} fbx-thermometer-item-label`} style={props.labelStyle ?? {}}>{item.label}</span>
                <span style={{ backgroundColor: item.color, ...props.valueStyle }}
                    className={`flex align-items-center justify-content-center ${props.valueClassName ?? ""}  fbx-thermometer-item-value`}>{item.value}</span>
            </span>
            <Tooltip target={`#${toolTipId}`} content={item.toolTip || ''} />
        </>)
    }



    return (
        <div id={_id} className={`flex ${_direction === "horizontal" ? 'flex-row' : 'flex-column'} fbx-thermometer ${_dirClassName} ${props.className || ''}`} style={props.style || {}}>
            {
                _value.map((item, index) => {

                    if (_direction === 'horizontal') {
                        return renderHorizontal(item, index);
                    } else {
                        // Vertical rendering can be added here in the future
                        return renderVertical(item, index);
                    }
                })

            }

        </div>
    )

}